/** Launch poll modal helpers aligned with backend period / audience semantics. */

import { regionOptions } from "../Constants/Constants";

export type CycleCadence = "quarterly" | "bi-quarterly" | "annual" | "one-off";

type ExecutiveRegion = "APAC" | "Americas" | "Latin America" | "EMEA";

export type TargetAudience = "all" | (string & {});

export type LaunchContextAudienceOption = {
  value: TargetAudience;
  label: string;
  disabled: boolean;
  disabled_reason?: string | null;
};

export type LaunchContextPayload = {
  can_launch: boolean;
  block_reason?: string | null;
  poll1_cycle_end_date: string;
  poll2_cycle_end_date: string;
  poll1_audience_options: LaunchContextAudienceOption[];
  poll2_audience_options: LaunchContextAudienceOption[];
  poll1_can_launch: boolean;
  poll2_can_launch: boolean;
  poll1_block_reason?: string | null;
  poll2_block_reason?: string | null;
  poll1_participant_block?: boolean;
  poll2_participant_block?: boolean;
};

export function hasParticipantLaunchBlocks(
  ctx: Pick<
    LaunchContextPayload,
    "poll1_participant_block" | "poll2_participant_block"
  >
): boolean {
  return Boolean(ctx.poll1_participant_block || ctx.poll2_participant_block);
}

const CANONICAL_EXECUTIVE_REGIONS: ExecutiveRegion[] = regionOptions.map(
  option => option.value as ExecutiveRegion
);

const LEGACY_TARGET_AUDIENCE_MAP: Record<string, string> = {
  amer: "Americas",
  emea: "EMEA",
  apac: "APAC",
};

function sortRegions(regions: Iterable<string>): string[] {
  return Array.from(regions).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

export function isBlankExecutiveRegion(
  value: string | null | undefined
): boolean {
  const trimmed = (value || "").trim();
  return !trimmed || trimmed === "N/A" || trimmed === "—";
}

function normalizeTargetAudienceValue(
  value: string | null | undefined
): TargetAudience {
  if (isBlankExecutiveRegion(value)) return "all";
  const trimmed = value!.trim();
  if (trimmed.toLowerCase() === "all") return "all";

  const legacy = LEGACY_TARGET_AUDIENCE_MAP[trimmed.toLowerCase()];
  if (legacy) return legacy;

  const canonical = CANONICAL_EXECUTIVE_REGIONS.find(
    region => region.toLowerCase() === trimmed.toLowerCase()
  );
  if (canonical) return canonical;

  return trimmed;
}

export function collectExecutiveRegionsFromSponsors(
  executives: { region?: string | null }[] | null | undefined
): string[] {
  const seen = new Map<string, string>();
  executives?.forEach(executive => {
    if (isBlankExecutiveRegion(executive.region)) return;
    const trimmed = executive.region!.trim();
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  });
  return sortRegions(seen.values());
}

export function parseTargetAudience(
  value: string,
  regions: readonly string[] = []
): TargetAudience {
  const normalized = normalizeTargetAudienceValue(value);
  if (normalized === "all") return "all";
  const match = regions.find(
    region => region.toLowerCase() === String(normalized).toLowerCase()
  );
  return (match ?? normalized) as TargetAudience;
}

export const CYCLE_CADENCE_OPTIONS: { value: CycleCadence; label: string }[] = [
  { value: "quarterly", label: "Quarterly" },
  { value: "bi-quarterly", label: "Bi-quarterly" },
  { value: "annual", label: "Annual" },
  { value: "one-off", label: "One-off" },
];

export function quarterPrefixFromPeriod(period: string): string {
  const m = period.match(/-Q(\d)$/i);
  if (m) return `Q${m[1]}`;
  if (period.endsWith("-H1")) return "H1";
  if (period.endsWith("-H2")) return "H2";
  if (period.endsWith("-oneoff")) return "One-off";
  return period;
}

export function composePollNameFromSuffix(
  period: string,
  suffix: string
): string {
  const prefix = quarterPrefixFromPeriod(period);
  const trimmed = suffix.trim();
  return trimmed ? `${prefix} ${trimmed}` : `${prefix} `;
}

export function isPollNameOnlyDefaultQuarter(
  pollNameSuffix: string,
  _period: string
): boolean {
  return pollNameSuffix.trim().length === 0;
}

function formatPeriodRangeDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function periodRangeLabel(start: Date, end: Date): string {
  return `${formatPeriodRangeDate(start)} – ${formatPeriodRangeDate(end)}`;
}

export function quarterDateRangeTooltip(period: string): string {
  const trimmed = period.trim();
  if (!trimmed) return "";

  const quarterMatch = trimmed.match(/^(\d{4})-Q([1-4])$/i);
  if (quarterMatch) {
    const year = Number(quarterMatch[1]);
    const quarter = Number(quarterMatch[2]);
    const startMonth = (quarter - 1) * 3;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0);
    return periodRangeLabel(start, end);
  }

  const h1Match = trimmed.match(/^(\d{4})-H1$/i);
  if (h1Match) {
    const year = Number(h1Match[1]);
    return periodRangeLabel(new Date(year, 0, 1), new Date(year, 5, 30));
  }

  const h2Match = trimmed.match(/^(\d{4})-H2$/i);
  if (h2Match) {
    const year = Number(h2Match[1]);
    return periodRangeLabel(new Date(year, 6, 1), new Date(year, 11, 31));
  }

  if (trimmed.endsWith("-oneoff")) {
    const yearMatch = trimmed.match(/^(\d{4})/);
    if (yearMatch) {
      const year = Number(yearMatch[1]);
      return periodRangeLabel(new Date(year, 0, 1), new Date(year, 11, 31));
    }
    return "One-off cycle";
  }

  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = Number(yearMatch[1]);
    return periodRangeLabel(new Date(year, 0, 1), new Date(year, 11, 31));
  }

  return trimmed;
}

export function periodForLaunch(cadence: CycleCadence | string): string {
  const d = new Date();
  const y = d.getFullYear();
  const month = d.getMonth() + 1;
  if (cadence === "annual") return `${y}`;
  if (cadence === "one-off") return `${y}-oneoff`;
  if (cadence === "bi-quarterly") {
    const half = month < 6 ? "H1" : "H2";
    return `${y}-${half}`;
  }
  const q = Math.floor((month - 1) / 3) + 1;
  return `${y}-Q${q}`;
}

export function todayDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isPastDueDate(dueDate: string): boolean {
  return Boolean(dueDate) && dueDate < todayDateString();
}

export function cycleEndDateDefault(
  cadence: CycleCadence | string = "quarterly"
): string {
  const d = new Date();
  const y = d.getFullYear();
  const month = d.getMonth() + 1;
  let end: Date;
  if (cadence === "annual" || cadence === "one-off") {
    end = new Date(y, 11, 31);
  } else if (cadence === "bi-quarterly") {
    end = month < 6 ? new Date(y, 5, 30) : new Date(y, 11, 31);
  } else {
    const q = Math.floor((month - 1) / 3);
    end = new Date(y, q * 3 + 3, 0);
  }
  const endStr = end.toISOString().slice(0, 10);
  const today = todayDateString();
  return endStr < today ? today : endStr;
}

export function poll1FrequencyFromConfig(
  config?: {
    cycle_config?: { poll1_frequency?: string; cadence?: string };
  } | null
): CycleCadence {
  const freq =
    config?.cycle_config?.poll1_frequency ?? config?.cycle_config?.cadence;
  if (
    freq === "quarterly" ||
    freq === "bi-quarterly" ||
    freq === "annual" ||
    freq === "one-off"
  ) {
    return freq;
  }
  return "quarterly";
}

export function poll2FrequencyFromConfig(
  config?: {
    cycle_config?: { poll2_frequency?: string; cadence?: string };
  } | null
): CycleCadence {
  const freq = config?.cycle_config?.poll2_frequency ?? "bi-quarterly";
  if (
    freq === "quarterly" ||
    freq === "bi-quarterly" ||
    freq === "annual" ||
    freq === "one-off"
  ) {
    return freq;
  }
  return "bi-quarterly";
}

export type LaunchPollFormState = {
  poll_name_suffix: string;
  poll_name_touched: boolean;
  poll1_enabled: boolean;
  poll2_enabled: boolean;
  poll1_target_audience: TargetAudience;
  poll2_target_audience: TargetAudience;
  poll1_due_date: string;
  poll2_due_date: string;
  reminder_cadence: string;
};

export type DiscoveryHubLaunchDefaults = {
  reminder_cadence?: string;
  target_audience?: string;
};

export function defaultLaunchFormState(
  poll1Cadence: CycleCadence | string = "quarterly",
  poll2Cadence: CycleCadence | string = "bi-quarterly",
  launchDefaults?: DiscoveryHubLaunchDefaults | null,
  regions: readonly string[] = []
): LaunchPollFormState {
  const reminder =
    launchDefaults?.reminder_cadence === "fortnightly" ||
    launchDefaults?.reminder_cadence === "off"
      ? launchDefaults.reminder_cadence
      : "weekly";
  const defaultAudience = parseTargetAudience(
    launchDefaults?.target_audience ?? "all",
    regions
  );
  return {
    poll_name_suffix: "",
    poll_name_touched: false,
    poll1_enabled: true,
    poll2_enabled: true,
    poll1_target_audience: defaultAudience,
    poll2_target_audience: defaultAudience,
    poll1_due_date: cycleEndDateDefault(poll1Cadence),
    poll2_due_date: cycleEndDateDefault(poll2Cadence),
    reminder_cadence: reminder,
  };
}

export function firstEnabledAudience(
  options: LaunchContextAudienceOption[] | undefined
): TargetAudience {
  const enabled = options?.find(opt => !opt.disabled);
  return (enabled?.value ?? "all") as TargetAudience;
}
